import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import apiClient, { getErrorMessage } from '../utils/axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const clearLocal = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  }, []);

  const logout = useCallback(async () => {
    if (localStorage.getItem('token')) {
      try {
        await apiClient.post('/auth/logout', { client: 'web' });
      } catch (error) {
        console.error('로그아웃 API 호출 실패:', error);
      }
    }
    clearLocal();
  }, [clearLocal]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('사용자 정보 조회 실패:', error);
      if (error.code !== 'ERR_NETWORK') clearLocal();
      return null;
    } finally {
      setLoading(false);
    }
  }, [clearLocal]);

  useEffect(() => {
    if (token) fetchUser();
    else setLoading(false);
  }, [token, fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await apiClient.post('/auth/login', { email, password, client: 'web' });
      localStorage.setItem('token', response.data.token);
      setToken(response.data.token);
      const me = await fetchUser();
      return { success: true, user: me };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error, '로그인에 실패했습니다.'),
        requiresVerification: error.response?.status === 403 && !!error.response?.data?.requiresVerification,
      };
    }
  };

  const register = async (email, username, phoneNumber, password, returnUrl) => {
    try {
      const response = await apiClient.post('/auth/register', {
        email,
        username,
        phoneNumber,
        password,
        returnUrl: returnUrl || undefined,
      });
      return { success: true, requiresVerification: true, email: response.data.email || email };
    } catch (error) {
      return { success: false, error: getErrorMessage(error, '회원가입에 실패했습니다.') };
    }
  };

  const value = { user, token, loading, login, register, logout, fetchUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
